import { useEffect, useState } from 'react';
import appScreenshot from '../assets/app-screenshot.png';
import insightsScreenshot from '../assets/insights-screenshot.png';
import libreviewScreenshot from '../assets/libreview-screenshot.png';
import insulinScreenshot from '../assets/insulin-screenshot.png';
import exerciseScreenshot from '../assets/exercise-screenshot.png';
import settingsScreenshot from '../assets/settings-screenshot.png';
import recordFoodScreenshot from '../assets/record-food-screenshot.png';
import './SupportPage.css';

function PhoneImage({ src, alt }) {
  return (
    <div className="section-phone">
      <div className="phone-mockup">
        <img src={src} alt={alt} className="phone-screenshot" />
      </div>
    </div>
  );
}

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'quick-start', label: 'Quick Start' },
  {
    id: 'pages', label: 'App Pages', children: [
      { id: 'page-home', label: 'Home' },
      { id: 'page-add-food', label: 'Add Food' },
      { id: 'page-add-insulin', label: 'Add Insulin' },
      { id: 'page-add-exercise', label: 'Add Exercise' },
      { id: 'page-insights', label: 'Insights' },
    ]
  },
  { id: 'settings', label: 'Settings' },
  { id: 'faqs', label: 'FAQs' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
  { id: 'privacy', label: 'Privacy & Medical Disclaimer' },
  { id: 'acknowledgments', label: 'Acknowledgments' },
];

function TocItem({ item, activeId }) {
  return (
    <li>
      <a
        href={`#${item.id}`}
        className={activeId === item.id ? 'toc-link active' : 'toc-link'}
      >
        {item.label}
      </a>
      {item.children && (
        <ul className="toc-sub">
          {item.children.map(child => (
            <TocItem key={child.id} item={child} activeId={activeId} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function SupportPage() {
  const [activeId, setActiveId] = useState('overview');
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    const allIds = TOC.flatMap(t => t.children ? [t.id, ...t.children.map(c => c.id)] : [t.id]);
    const observers = [];

    allIds.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveId(id); },
        { rootMargin: '-20% 0px -70% 0px' }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <div className="support-page">
      <div className="support-header">
        <div className="support-header-inner">
          <h1>Support</h1>
          <p className="support-lead">
            Documentation for managing your blood glucose, meals, insulin, and exercise — all in one app.
          </p>
        </div>
      </div>

      <div className="support-layout">

        {/* Mobile TOC toggle */}
        <button
          className="toc-toggle"
          onClick={() => setTocOpen(o => !o)}
          aria-expanded={tocOpen}
        >
          {tocOpen ? '✕ Close' : '☰ Contents'}
        </button>

        {/* Sidebar TOC */}
        <aside className={`support-toc ${tocOpen ? 'open' : ''}`}>
          <div className="toc-sticky">
            <p className="toc-heading">Contents</p>
            <nav>
              <ul className="toc-list">
                {TOC.map(item => (
                  <TocItem key={item.id} item={item} activeId={activeId} />
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <article className="support-content">

          {/* ── OVERVIEW ── */}
          <section id="overview">
            <h2>Overview</h2>
            <p>
              <strong>Trenda</strong> is a mobile app designed to help people with diabetes
              track and visualise their blood glucose readings alongside meals, insulin doses, and
              exercise — giving you a clearer picture of how daily activities affect your levels.
            </p>
            <p>Key capabilities:</p>
            <ul>
              <li>Automatic blood glucose sync from <strong>LibreView</strong> (FreeStyle Libre CGM data)</li>
              <li>Real-time trend charts with customisable time intervals</li>
              <li>Configurable low/high alarms with day and night schedules</li>
              <li>Meal logging with optional photo, cookbook reference, online recipe link, or manual food lookup</li>
              <li>Insulin dose tracking for bolus and basal insulins</li>
              <li>Exercise logging</li>
              <li>Insights screen that overlays meals and insulin on your glucose chart</li>
            </ul>
          </section>

          {/* ── QUICK START ── */}
          <section id="quick-start">
            <h2>Quick Start</h2>
            <p>
              Trenda reads directly from the Libre API. In order for Trenda to display blood glucose
              readings, you must first create a LibreLinkUp account.
            </p>
            <p>
              To register for a LibreLinkUp account when the person wearing the sensor and the
              “Connection” are the same person, open the FreeStyle Libre / LibreLink app, make sure
              you are signed into your LibreView account, go to the menu, tap <strong>Share</strong>{' '}
              or <strong>Connected Apps</strong>, select <strong>LibreLinkUp</strong>, and choose{' '}
              <strong>Add</strong> (or <strong>Invite</strong>) Connection. Enter your own name and
              your own email address to send an invitation to yourself.
            </p>
            <p>
              Next, open the email invitation you receive, tap the link or button to download or
              open the LibreLinkUp app, tap <strong>Get Started</strong>, choose your country or
              region, accept the licence and privacy terms, and create a LibreLinkUp account using
              that same email address. Then verify your account from your inbox.
            </p>
            <p>
              Finally, sign into the LibreLinkUp app with your verified email. When the prompt
              appears showing that your FreeStyle Libre app wants to share glucose data, tap{' '}
              <strong>Accept</strong> so that you can view your own shared glucose readings in
              LibreLinkUp.
            </p>
            <p>Get up and running in three steps:</p>
            <div className="section-with-image">
              <PhoneImage src={libreviewScreenshot} alt="LibreView credentials screen in Trenda Settings" />
              <div className="section-body">

                <h4>Before you begin: set up LibreLinkUp</h4>
                <p>
                  Trenda connects to your glucose data through Abbott's <strong>LibreLinkUp</strong> service.
                  To set this up:
                </p>
                <ol>
                  <li>Open your <strong>FreeStyle Libre</strong> app.</li>
                  <li>Create or sign in with a <strong>LibreLinkUp</strong> account.</li>
                  <li>Link your Libre device to that account.</li>
                </ol>
                <p>
                  Once connected, Trenda can securely read your glucose data in real time
                  using LibreLinkUp. Your data remains private and is only shared with your permission.
                </p>

                <h4>Then, in Trenda</h4>
                <ol>
                  <li>
                    <strong>Connect your LibreLinkUp account</strong><br />
                    Open <em>Settings → Blood Glucose Data Source</em>, select <em>LibreView</em>, enter
                    your LibreLinkUp email and password, then tap <em>Test Connection</em>. Once
                    successful, the app will automatically fetch your glucose readings.
                  </li>
                  <li>
                    <strong>Record your first meal</strong><br />
                    From the Home screen footer, tap <strong>Add Food</strong>. Choose a meal type
                    (Breakfast, Lunch, Dinner, or Snack), describe what you ate, pick the date/time,
                    and save.
                  </li>
                  <li>
                    <strong>Check the Insights screen</strong><br />
                    Tap the <strong>Insights</strong> icon in the footer to see your blood glucose chart.
                    Use the floating action button to toggle meal and insulin overlays on the graph.
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* ── APP PAGES ── */}
          <section id="pages">
            <h2>App Pages</h2>

            <article id="page-home">
              <h3>Home</h3>
              <p>The Home screen is your dashboard — everything you need at a glance.</p>
              <div className="section-with-image">
                <PhoneImage src={appScreenshot} alt="Trenda Home screen showing blood glucose reading and trend chart" />
                <div className="section-body">
                  <h4>Latest Blood Glucose Reading</h4>
                  <p>A large card displays:</p>
                  <ul>
                    <li><strong>Current value</strong> in your chosen unit (mg/dL or mmol/L)</li>
                    <li><strong>Trend arrow</strong> showing direction (rising, falling, steady, etc.)</li>
                    <li><strong>Time ago</strong> since the reading (e.g., "3 m ago")</li>
                  </ul>
                  <p>Tap this card to view all readings from the past hour in a pop-up list.</p>

                  <h4>Last Insulin Dose</h4>
                  <p>Shows your most recent insulin record — name, dose, and how long ago. Tap to see all doses from the last 12 hours.</p>

                  <h4>Blood Glucose Trend Chart</h4>
                  <p>An interactive chart shows glucose over time. Use the segment selector at the bottom to choose <strong>3 h</strong>, <strong>6 h</strong>, <strong>12 h</strong>, <strong>24 h</strong>, or <strong>48 h</strong>.</p>

                  <h4>Snooze Indicator</h4>
                  <p>In the top-left, a snooze icon appears orange with a badge when any alarms are snoozed. Tap it to view and manage active snoozes.</p>

                  <h4>Pull to Refresh</h4>
                  <p>Pull down anywhere on the screen to manually refresh your glucose data.</p>

                  <h4>Footer Navigation</h4>
                  <p>Four icons at the bottom let you quickly jump to:</p>
                  <ul>
                    <li><strong>Add Food</strong></li>
                    <li><strong>Add Insulin</strong></li>
                    <li><strong>Add Exercise</strong></li>
                    <li><strong>Insights</strong></li>
                  </ul>
                </div>
              </div>
            </article>

            <article id="page-add-food">
              <h3>Add Food</h3>
              <p>Recording meals helps you see how food affects your glucose.</p>
              <div className="section-with-image">
                <PhoneImage src={recordFoodScreenshot} alt="Trenda Record Lunch screen showing meal description, time picker and food photo" />
                <div className="section-body">
                  <h4>Step 1 – Choose Input Method</h4>
                  <p>At the top of the screen, pick how you want to log the meal:</p>
                  <table>
                    <thead><tr><th>Method</th><th>Best for</th></tr></thead>
                    <tbody>
                      <tr><td><strong>Standard</strong></td><td>Quick entry with optional photo + auto-nutrition analysis</td></tr>
                      <tr><td><strong>Cookbook</strong></td><td>Referencing a physical cookbook (author, title, page number)</td></tr>
                      <tr><td><strong>Online</strong></td><td>Pasting a recipe URL from the web</td></tr>
                      <tr><td><strong>Manual</strong></td><td>Searching a food database and adding items one-by-one</td></tr>
                    </tbody>
                  </table>

                  <h4>Step 2 – Select Meal Type</h4>
                  <p>Tap <strong>Breakfast</strong>, <strong>Lunch</strong>, <strong>Dinner</strong>, or <strong>Snack</strong>. The app pre-fills the time based on your default meal times in Settings.</p>

                  <h4>Step 3 – Describe &amp; Save</h4>
                  <ul>
                    <li><strong>What Did You Eat</strong> — free-text description (required)</li>
                    <li><strong>Date / Time</strong> — adjust using the calendar and time picker; quick-time buttons let you pick "Now", "15 m ago", "30 m ago", or "1 h ago"</li>
                    <li><strong>Photo / Nutrition</strong> — depending on input method, you may add a photo and view detected nutrition (carbs, protein, fat, calories)</li>
                  </ul>
                  <p>Tap <strong>Save</strong> to store the meal. You'll see a summary screen where you can optionally record a bolus insulin dose before returning home.</p>
                </div>
              </div>
            </article>

            <article id="page-add-insulin">
              <h3>Add Insulin</h3>
              <p>Track every dose to correlate with your glucose chart.</p>
              <div className="section-with-image">
                <PhoneImage src={insulinScreenshot} alt="Trenda Record Insulin screen showing bolus/basal selection and dose picker" />
                <div className="section-body">
                  <h4>Bolus / Basal Selection</h4>
                  <p>Tap the button for your insulin type. The label shows the brand you've configured in Settings (e.g., <em>Humalog</em> for bolus, <em>Lantus</em> for basal).</p>

                  <h4>Dose Picker</h4>
                  <p>A slot-machine style selector lets you dial in the dose from <strong>0.5</strong> to <strong>100 units</strong> in 0.5-unit increments.</p>

                  <h4>Date &amp; Time</h4>
                  <p>Same controls as Add Food: calendar picker, time picker, and quick-time buttons.</p>

                  <h4>Save</h4>
                  <p>Tap <strong>Save</strong> to record the dose. You'll receive a confirmation and return to the previous screen.</p>
                </div>
              </div>
            </article>

            <article id="page-add-exercise">
              <h3>Add Exercise</h3>
              <p>Exercise can lower blood glucose — logging it helps explain dips on your chart.</p>
              <div className="section-with-image">
                <PhoneImage src={exerciseScreenshot} alt="Trenda Record Exercise screen showing exercise type, start time and duration pickers" />
                <div className="section-body">
                  <h4>Exercise Type</h4>
                  <p>Choose from a dropdown list of common activities (Walking, Running, Cycling, Swimming, Gym, Yoga, etc.).</p>

                  <h4>Start Time</h4>
                  <p>Date and time pickers work the same way as on other screens, with quick-time buttons for convenience.</p>

                  <h4>Duration</h4>
                  <p>Two slot-machine selectors let you pick <strong>Hours</strong> (0–23) and <strong>Minutes</strong> (0–59).</p>

                  <h4>Add Snack Shortcut</h4>
                  <p>A link at the bottom lets you quickly record a snack if you ate something during or after exercise.</p>

                  <h4>Save</h4>
                  <p>Tap <strong>Save</strong> to store the exercise record.</p>
                </div>
              </div>
            </article>

            <article id="page-insights">
              <h3>Insights</h3>
              <p>The Insights screen is where data comes together — see how meals and insulin relate to glucose movements.</p>
              <div className="section-with-image">
                <PhoneImage src={insightsScreenshot} alt="Trenda Insights screen showing blood glucose, insulin profile, carbohydrate and exercise charts" />
                <div className="section-body">
                  <h4>Blood Glucose Chart</h4>
                  <p>A larger, more detailed chart than on Home. Use the dropdown in the top-right to switch between time intervals (3 h – 48 h).</p>

                  <h4>Overlay Settings (Floating Button)</h4>
                  <p>Tap the <strong>layers</strong> button to open the overlay modal. Toggle:</p>
                  <ul>
                    <li><strong>Show Meals</strong> — displays meal markers on the chart; tap a marker to see meal details</li>
                    <li><strong>Show Insulin</strong> — displays insulin dose markers; tap to view dose details</li>
                  </ul>

                  <h4>Insulin Navigation Panel</h4>
                  <p>When the insulin overlay is enabled and you have doses in the selected period, a navigation panel appears below the chart. Use the arrows to step through each dose; the chart highlights the corresponding marker.</p>

                  <h4>Refresh</h4>
                  <p>Tap the refresh icon in the app bar, or pull down on the screen, to reload data.</p>
                </div>
              </div>
            </article>
          </section>

          {/* ── SETTINGS ── */}
          <section id="settings">
            <h2>Settings</h2>
            <p>Access Settings by tapping the gear icon on the Home screen.</p>
            <div className="section-with-image">
              <PhoneImage src={settingsScreenshot} alt="Trenda Settings screen showing expandable sections" />
              <div className="section-body">

                <h3 id="settings-data-source">Blood Glucose Data Source</h3>
                <p>Choose where the app reads glucose data:</p>
                <ul>
                  <li><strong>Apple HealthKit</strong> — (coming soon) reads directly from the Health app on iOS</li>
                  <li><strong>LibreView</strong> — connects to your Abbott LibreView account to fetch FreeStyle Libre data</li>
                </ul>
                <p>If you select LibreView, enter your <strong>Email</strong> and <strong>Password</strong>, then tap <strong>Test Connection</strong>. A success or error message will appear.</p>

                <h3 id="settings-unit">Blood Glucose Measurements</h3>
                <p>Select your preferred unit:</p>
                <ul>
                  <li><strong>mg/dL</strong> — milligrams per deciliter (common in US)</li>
                  <li><strong>mmol/L</strong> — millimoles per liter (common in UK, EU, Australia)</li>
                </ul>
                <p>All readings, thresholds, and charts will display in the unit you choose.</p>

                <h3 id="settings-interval">Blood Glucose Refresh Interval</h3>
                <p>How often the app automatically fetches new readings:</p>
                <ul>
                  <li><strong>1 minute</strong></li>
                  <li><strong>2 minutes</strong></li>
                  <li><strong>5 minutes</strong> (default)</li>
                </ul>
                <p>Shorter intervals give faster updates but may use more battery.</p>

                <h3 id="settings-banners">BG Reading Banners</h3>
                <p>When enabled, a banner pops up each time a new glucose reading arrives.</p>
                <ul>
                  <li><strong>Show BG Reading Banners</strong> — master toggle</li>
                  <li><strong>Show When App is Active</strong> — display banner overlay while using the app</li>
                  <li><strong>Show on Lock Screen</strong> — send a notification when the device is locked</li>
                  <li><strong>Auto-Dismiss Timeout</strong> — how many seconds (0–60) until the banner disappears automatically</li>
                </ul>

                <h3 id="settings-target">Target Range</h3>
                <p>The green band on charts represents your target glucose range. Choose a preset or define custom values:</p>
                <table>
                  <thead><tr><th>Preset</th><th>Low</th><th>High</th></tr></thead>
                  <tbody>
                    <tr><td>Tight</td><td>72 mg/dL (4.0 mmol/L)</td><td>126 mg/dL (7.0 mmol/L)</td></tr>
                    <tr><td>Relaxed</td><td>72 mg/dL (4.0 mmol/L)</td><td>162 mg/dL (9.0 mmol/L)</td></tr>
                    <tr><td>Custom</td><td colSpan={2}>Set your own low and high values</td></tr>
                  </tbody>
                </table>

                <h3 id="settings-alarms">Blood Glucose Alarms</h3>
                <p>Configure alerts when your glucose crosses certain thresholds or changes rapidly.</p>

                <h4>Alarm Types</h4>
                <table>
                  <thead><tr><th>Alarm</th><th>Default Threshold</th><th>Description</th></tr></thead>
                  <tbody>
                    <tr><td>Going Low</td><td>70 mg/dL (3.9 mmol/L)</td><td>Approaching low range</td></tr>
                    <tr><td>Very Low</td><td>54 mg/dL (3.0 mmol/L)</td><td>Critically low</td></tr>
                    <tr><td>Going High</td><td>144 mg/dL (8.0 mmol/L)</td><td>Approaching high range</td></tr>
                    <tr><td>Very High</td><td>180 mg/dL (10.0 mmol/L)</td><td>Critically high</td></tr>
                    <tr><td>Missed Reading</td><td>30 minutes</td><td>No new reading received</td></tr>
                    <tr><td>Fast Rise</td><td>10.8 mg/dL/min (0.6 mmol/L/min)</td><td>Rapid increase</td></tr>
                    <tr><td>Fast Drop</td><td>10.8 mg/dL/min (0.6 mmol/L/min)</td><td>Rapid decrease</td></tr>
                  </tbody>
                </table>

                <h4>Per-Alarm Settings</h4>
                <ul>
                  <li><strong>Enable / Disable</strong> — toggle</li>
                  <li><strong>Threshold</strong> (or Timeout for Missed Reading)</li>
                  <li><strong>Sound</strong> — choose from Apple system sounds or custom alert tones</li>
                  <li><strong>Default Snooze Interval</strong> — minutes to silence the alarm when snoozed</li>
                </ul>

                <h4>Day / Night Schedule</h4>
                <p>Enable to use different thresholds during daytime and nighttime hours. Tap the day or night card to set the start times (e.g., Day starts 07:00, Night starts 22:00).</p>

                <h3 id="settings-insulin">Insulin Settings</h3>
                <ul>
                  <li><strong>Bolus Insulin</strong> — select brand (Humalog, NovoRapid, Apidra, Fiasp, etc.)</li>
                  <li><strong>Basal Insulin</strong> — select brand (Lantus, Levemir, Tresiba, etc.)</li>
                  <li><strong>Default Basal Dose</strong> — pre-filled when recording basal</li>
                  <li><strong>Default Basal Time</strong> — pre-filled time of day</li>
                  <li><strong>Basal Insulin Reminder</strong> — toggle to receive a daily notification at your default basal time</li>
                </ul>

                <h3 id="settings-meals">Default Meal Times</h3>
                <p>Set typical times for each meal. When you start recording a meal, the time picker will default to this value.</p>
                <ul>
                  <li><strong>Breakfast</strong> (default 08:00)</li>
                  <li><strong>Lunch</strong> (default 12:00)</li>
                  <li><strong>Dinner</strong> (default 18:00)</li>
                </ul>
                <p>Snacks always default to the current time.</p>

                <h3 id="settings-records">View All Stored Records</h3>
                <p>Tap to open a screen listing all your insulin, meal, and exercise records. You can review, edit, or delete entries from here.</p>

                <h3 id="settings-diagnostics">Diagnostics Logging</h3>
                <p>Enable this toggle to print debug logs for blood glucose fetching, chart rendering, and alarm evaluation. Useful for troubleshooting; keep disabled for normal use.</p>

              </div>
            </div>
          </section>

          {/* ── FAQs ── */}
          <section id="faqs">
            <h2>FAQs</h2>

            <h4>How does the app get my glucose readings?</h4>
            <p>Trenda connects to <strong>LibreView</strong>, Abbott's cloud service that stores data from FreeStyle Libre sensors. The app periodically fetches your latest readings using your LibreView credentials.</p>

            <h4>Is my LibreView password stored securely?</h4>
            <p>Yes. Credentials are saved in <strong>iOS Keychain</strong> (encrypted secure storage) and are never sent to any server other than LibreView itself.</p>
            <table>
              <thead>
                <tr><th colSpan={2}>Summary of Protection Layers</th></tr>
                <tr><th>Layer</th><th>Mechanism</th></tr>
              </thead>
              <tbody>
                <tr><td>At rest (on device)</td><td>iOS Keychain</td></tr>
                <tr><td>In memory</td><td>Short-lived cache; never logged or persisted to plain storage</td></tr>
                <tr><td>In transit</td><td>HTTPS (TLS) only</td></tr>
                <tr><td>After login</td><td>Auth token used; password not sent again until token expires</td></tr>
                <tr><td>Diagnostics logs</td><td>Only status codes and truncated response bodies are logged — never credentials</td></tr>
              </tbody>
            </table>

            <h4>Why is there a delay between my sensor reading and the app?</h4>
            <p>LibreView data syncs from your sensor through the Libre app to Abbott's servers, then to Trenda. This can introduce a few minutes of lag. Reducing the refresh interval in Settings may help, but some delay is unavoidable.</p>

            <h4>Can I use the app without a CGM?</h4>
            <p>Currently, glucose data requires a FreeStyle Libre sensor linked to LibreView. You can still use the meal, insulin, and exercise tracking features without glucose data, but the charts will be empty.</p>

            <h4>What happens if I travel across time zones?</h4>
            <p>The app stores timestamps in UTC and displays them in your device's local time. When you change time zones, past events stay accurate; new entries use the new local time.</p>

            <h4>How do I change the alarm sound?</h4>
            <p>Go to <em>Settings → Blood Glucose Alarms</em>, tap the alarm you want to configure, then tap <strong>Sound</strong> to choose from available tones. You can preview each sound before selecting.</p>

            <h4>Can I export my data?</h4>
            <p>An export feature is planned for a future release. For now, all data remains on your device.</p>

            <h4>Does the app work on Android?</h4>
            <p>Trenda is currently iOS-only. Android support is under consideration.</p>
          </section>

          {/* ── TROUBLESHOOTING ── */}
          <section id="troubleshooting">
            <h2>Troubleshooting</h2>

            <h4>LibreView connection fails</h4>
            <ul>
              <li>Double-check your email and password — credentials are case-sensitive.</li>
              <li>Make sure your LibreView account is active and you can log in on the <a href="https://www.libreview.com" target="_blank" rel="noopener noreferrer">LibreView website</a>.</li>
              <li>If you recently changed your password, update it in Settings.</li>
              <li>Try toggling Airplane Mode on and off, then retry.</li>
            </ul>

            <h4>No glucose readings appear</h4>
            <ul>
              <li>Ensure your FreeStyle Libre sensor is paired with the Libre app and syncing to LibreView.</li>
              <li>Open the Libre app and perform a scan to push the latest data to the cloud.</li>
              <li>Check that your LibreView region is supported (US, EU, AU, etc.).</li>
              <li>Pull down on the Home screen to force a refresh.</li>
            </ul>

            <h4>Readings are stale or delayed</h4>
            <ul>
              <li>Reduce the <em>Refresh Interval</em> to 1 or 2 minutes in Settings.</li>
              <li>Make sure Background App Refresh is enabled for Trenda in iOS Settings.</li>
              <li>Keep the Libre app running in the background so it continues uploading data.</li>
            </ul>

            <h4>Alarms not sounding</h4>
            <ul>
              <li>Check that the specific alarm is <strong>enabled</strong> in Settings.</li>
              <li>Ensure your device is not in <strong>Silent Mode</strong> or <strong>Do Not Disturb</strong>.</li>
              <li>Verify that notification permissions are granted for Trenda in iOS Settings → Notifications.</li>
              <li>Make sure the alarm is not currently snoozed (check the snooze icon on Home).</li>
            </ul>

            <h4>Banners not appearing</h4>
            <ul>
              <li>Go to <em>Settings → BG Reading Banners</em> and ensure the master toggle is on.</li>
              <li>If you only want lock-screen notifications, enable <strong>Show on Lock Screen</strong>.</li>
              <li>Check iOS notification settings for the app (allow banners and sounds).</li>
            </ul>

            <h4>App crashes or freezes</h4>
            <ul>
              <li>Force-quit the app and reopen it.</li>
              <li>Restart your device.</li>
              <li>If the problem persists, enable <em>Diagnostics Logging</em> in Settings and note any console errors, then <a href="mailto:support@betterinsights.app">contact support</a>.</li>
            </ul>
          </section>

          {/* ── PRIVACY ── */}
          <section id="privacy">
            <h2>Privacy &amp; Medical Disclaimer</h2>

            <div className="disclaimer-box">
              <strong>Important:</strong> Trenda is <u>not</u> a medical device and is not
              intended to diagnose, treat, cure, or prevent any disease. Always consult a qualified
              healthcare professional before making changes to your diabetes management.
            </div>

            <h4>Data Privacy</h4>
            <ul>
              <li>All meal, insulin, and exercise records are stored <strong>locally on your device</strong>.</li>
              <li>LibreView credentials are stored in the iOS Keychain and used only to fetch glucose data from Abbott's servers.  The data is used by a backend web srvice, which is protected and encrypts the data at rest at rest and in transit.</li>
              <li>Trenda does not transmit your health data to any third-party servers.</li>
              <li>Photo analysis for nutrition uses the FatSecret API; images are sent securely and are not stored by FatSecret beyond processing.</li>
            </ul>

            <h4>Medical Disclaimer</h4>
            <p>
              The information provided by Trenda — including glucose readings, charts,
              trends, and alarms — is for informational purposes only. It should not replace
              professional medical advice, diagnosis, or treatment. Do not adjust insulin doses or
              make other medical decisions based solely on data from this app without consulting your
              doctor or diabetes care team.
            </p>

            <h4>Limitation of Liability</h4>
            <p>
              The developers of Trenda disclaim any liability for harm arising from reliance
              on the app's data or features. Use at your own risk.
            </p>
          </section>

          {/* ── ACKNOWLEDGMENTS ── */}
          <section id="acknowledgments">
            <h2>Acknowledgments</h2>
            <p>Trenda is built on the shoulders of some excellent open platforms and tools. Thank you to:</p>
            <ul>
              <li>
                <a href="https://platform.fatsecret.com/" target="_blank" rel="noopener noreferrer">FatSecret Platform</a> — nutritional data API used for photo-based meal analysis
              </li>
              <li>
                <a href="https://archive.org/" target="_blank" rel="noopener noreferrer">Internet Archive</a> — cookbook information and reference data
              </li>
              <li>
                The <strong>Flutter team</strong> at Google — for the remarkable cross-platform framework that powers the app
              </li>
            </ul>
          </section>

          <footer className="support-foot">
            Trenda Support Documentation &middot; Last updated February 2026
          </footer>

        </article>
      </div>
    </div>
  );
}
